import {useRef, useState, useCallback, useEffect} from "react";
import PropTypes from "prop-types";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import {cn} from "@/utils/cn";
import {observer} from "mobx-react";
import useCustomEventListener from "@/hooks/useCustomEventListener";
import {product as productStore} from "@/store/Product";

/**
 * Product Gallery component
 *
 * @param {Object[]} images - Product Gallery Images
 * @param {string} images[].src - The image's source / url.
 * @param {string} images[].srcset - The image's srcset attribute.
 * @param {string} images[].sizes - The image's sizes attribute.
 * @param {string} images[].alt - The image's alt (SEO).
 */
const ProductGallery = observer(({images, bottles}) => {
  const galleryRef = useRef();
  const [slideIndex, setSlideIndex] = useState(0);
  let {quantity} = productStore;
  // if quantity is 6 we still want to select the last image which in position 4
  if(quantity === 6) {
    quantity = 4;
  }

  // https://github.com/YIZHUANG/react-multi-carousel/issues/407#issuecomment-1741530668
  const handleThumbnailSelected = useCallback(
    i => {
      if (galleryRef.current) {
        galleryRef.current.goToSlide(i + 2);
      }
    },
    [galleryRef],
  );

  const handleSlideUpdate = payload => {
    const {index} = payload;
    handleThumbnailSelected(index);
  };

  useCustomEventListener("slide:update", handleSlideUpdate);

  // https://github.com/YIZHUANG/react-multi-carousel/issues/407#issuecomment-1741530668
  const changeSlide = (previousSlide, currentSlide, dataSize) => {
    let activeSlide = 0;
    // right arrow
    if (previousSlide < currentSlide)
      activeSlide = currentSlide - 2 === dataSize ? 0 : currentSlide - 2;
    // left arrow
    else
      activeSlide =
        currentSlide +
        (currentSlide <= dataSize && currentSlide >= 2 ? -2 : dataSize - 2);
    setSlideIndex(activeSlide);
  };

  useEffect(() => {
    const ssrImageEl = document.getElementById('ssr-product-gallery');
    if(ssrImageEl) {
      setTimeout(() => {
        ssrImageEl.style.display = 'none';
      }, 100);
    }
  }, []);

  return (
    <div className="relative h-full">
      <Carousel
        infinite
        ssr={true}
        renderArrowsWhenDisabled={true}
        afterChange={(previousSlide, {currentSlide}) =>
          changeSlide(previousSlide, currentSlide, images.length)
        }
        customLeftArrow={<CustomLeftArrow />}
        customRightArrow={<CustomRightArrow />}
        className="relative h-full w-full rounded-2xl md:rounded-3xl [&:hover+.thumbnails]:!opacity-100 [&_.react-multi-carousel-track]:mb-0 [&_.react-multi-carousel-track]:h-full"
        ref={galleryRef}
        arrows={images.length > 1}
        responsive={{
          mobile: {
            breakpoint: {max: Infinity, min: 0},
            items: 1,
          },
        }}
      >
        {images.map((img, i) => {
          return (
            <div
              key={i}
              className={cn(
                "bg relative mx-auto h-full w-full rounded-2xl max-lg:mx-auto md:rounded-3xl",
              )}
            >
              <img
                loading={i < 1 ? "eager" : "lazy"}
                className="mx-auto h-full overflow-hidden rounded-2xl object-cover object-center max-sm:w-full min-[530px]:w-full min-[840px]:w-auto"
                src={img.src}
                alt={img.alt}
                srcSet={img.srcset}
                sizes={img.sizes}
              />
              {i === 0 &&
                bottles?.map((bottle, j) => (
                  <img
                    key={j}
                    loading={"lazy"}
                    className={cn(
                      "absolute left-1/2 top-0 h-full w-full -translate-x-1/2 rounded-2xl object-cover object-center transition-opacity bg-gray md:rounded-3xl min-[840px]:w-auto",
                      j + 2 === quantity ? "opacity-100" : "opacity-0",
                    )}
                    src={bottle.src}
                    alt={bottle.alt}
                    srcSet={bottle.srcset}
                    sizes={bottle.sizes}
                  />
                ))}
            </div>
          );
        })}
      </Carousel>
      {/* Thumbnails */}
      <div className="thumbnails absolute left-[-55px] top-4 z-elevated hidden max-h-[calc(100%-6rem)] flex-col gap-2 overflow-x-hidden overflow-y-auto opacity-0 transition-opacity hover:opacity-100 lg:flex">
        {images.length > 1 && images.map((img, i) => (
          <button
            key={i}
            className={cn(
              "relative aspect-square min-h-12 min-w-12 overflow-hidden rounded-full border-2 object-contain drop-shadow transition-all md:rounded-full",
              slideIndex === i ? "border-white" : "border-transparent",
            )}
            onKeyDown={() => handleThumbnailSelected(i)}
            onClick={() => handleThumbnailSelected(i)}
          >
            <img
              loading={i < 1 ? "eager" : "lazy"}
              className="object-cover"
              src={img.srcset.split(" ")[0]}
              alt={img.alt}
              width={48}
              height={48}
            />
          </button>
        ))}
      </div>
    </div>
  );
});

ProductGallery.propTypes = {
  images: PropTypes.array.isRequired,
  bottles: PropTypes.array,
};

const CustomLeftArrow = ({onClick}) => {
  return (
    <button
      className="absolute bottom-2 left-2 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white drop-shadow md:bottom-4 md:left-4"
      onClick={onClick}
      aria-label="Left arrow"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="15"
        viewBox="0 0 18 15"
        fill="none"
      >
        <path
          d="M8.23978 14.7764L3.10627 8.7332H18V6.78596H3.13897L8.23978 0.776367H6.04905L0 7.75958L6.04905 14.7764H8.23978Z"
          fill="#304F46"
        />
      </svg>
    </button>
  );
};

const CustomRightArrow = ({onClick}) => {
  return (
    <button
      className="absolute bottom-2 right-2 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white drop-shadow md:bottom-4 md:right-4"
      onClick={onClick}
      aria-label="Right arrow"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="15"
        viewBox="0 0 18 15"
        fill="none"
      >
        <path
          d="M9.76022 14.7764L14.8937 8.7332H0L0 6.78596H14.861L9.76022 0.776367H11.951L18 7.75958L11.951 14.7764H9.76022Z"
          fill="#304F46"
        />
      </svg>
    </button>
  );
};

CustomLeftArrow.propTypes = {
  onClick: PropTypes.func,
};
CustomRightArrow.propTypes = {
  onClick: PropTypes.func,
};

export default ProductGallery;
